import { Injectable, NgZone, inject } from '@angular/core';
import { AnalyticsService } from '../analytics/analytics.service';
import {
  FirebaseAuthentication,
  SendSignInLinkToEmailOptions,
} from '@capacitor-firebase/authentication';
import { EventName, EventParam } from '../analytics/event-names';
import { SignOutService } from '../sign-out/sign-out.service';
import { DataLayerService } from '../datalayer/datalayer.service';
import { environment } from 'src/environments/environment';
import { ProfileService } from 'src/app/core/services/profile/profile.service';
import { Router } from '@angular/router';
import { StorageService } from '../storage/storage.service';
import { isNotNil } from 'src/app/shared/utils/app.utils';
import config from 'capacitor.config';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { Capacitor } from '@capacitor/core';
import { PAGE } from 'src/app/routes';
import { Token } from './token';
import { JobService } from '../job/job.service';
import { Job } from '../job/job';
import { WagerApi } from '../../api/wager/wager-api';
import { HttpService } from '../http/http.service';

const AUTH_USER = 'AUTH_USER';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private profileService = inject(ProfileService);
  private analyticsService = inject(AnalyticsService);
  private signOutService = inject(SignOutService);
  private dataLayerService = inject(DataLayerService);
  private storageService = inject(StorageService);
  private httpService = inject(HttpService);
  private router = inject(Router);
  private ngZone = inject(NgZone);
  private jobService = inject(JobService);

  private lastEmailLink: string | undefined;

  constructor() {
    FirebaseAuthentication.removeAllListeners().then(async () => {
      FirebaseAuthentication.addListener('authStateChange', (change) => {
        this.ngZone.run(async () => {
          if (change.user) {
            await this.storageService.setItem(AUTH_USER, change.user.uid);
            await this.setUser(change.user.uid);
          } else if (await this.userExists()) {
            await this.analyticsService.handleError('AuthService: Unexpected empty user');
            await this.signOut();
            this.router.navigate([PAGE.LOADING]);
          }
        });
      });
    });
  }

  get lastEmailLinkUsed(): string | undefined {
    return this.lastEmailLink;
  }

  async userExists(): Promise<boolean> {
    return isNotNil(await this.storageService.getItem(AUTH_USER));
  }

  isAuthenticated(): Job<boolean> {
    return this.jobService.create<boolean>(async () => {
      const userResult = await this.withFirebaseGuard(
        () => FirebaseAuthentication.getCurrentUser(),
        'Auth isAuthenticated',
      );
      return userResult.user ? true : false;
    });
  }

  setLastEmailLink(emailLink: string) {
    this.lastEmailLink = emailLink;
  }

  isSignInWithEmailLink(emailLink: string): Job<boolean> {
    return this.jobService.create<boolean>(async () => {
      const result = await this.withFirebaseGuard(
        () =>
          FirebaseAuthentication.isSignInWithEmailLink({
            emailLink,
          }),
        'Auth isSignInWithEmailLink',
      );
      return result.isSignInWithEmailLink;
    });
  }

  sendSignInLinkToEmail(email: string): Job<void> {
    return this.jobService.create(async () => {
      const options: SendSignInLinkToEmailOptions = {
        email,
        actionCodeSettings: {
          // URL you want to redirect back to.
          // URL must be in the authorized domains list in the Firebase Console.
          url: environment.emailLinkRedirectUri,
          // This must be true.
          handleCodeInApp: true,
          iOS: {
            bundleId: 'com.thndrgames.clinch',
          },
          android: {
            packageName: 'com.thndrgames.clinch',
            installApp: true,
            minimumVersion: '12', // Minimum Android SDK version
          },
        },
      };
      await this.withFirebaseGuard(
        () => FirebaseAuthentication.sendSignInLinkToEmail(options),
        'Auth sendSignInLinkToEmail',
      );

      this.analyticsService.log(EventName.SignInApiResponse, {
        provider: EventParam.SendEmailLink,
        response: EventParam.Success,
      });
    });
  }

  signInWithGoogle(): Job<void> {
    return this.jobService.create(async () => {
      await this.withFirebaseGuard(
        () => FirebaseAuthentication.signInWithGoogle(),
        'Auth signInWithGoogle',
      );
      this.analyticsService.log(EventName.SignInApiResponse, {
        provider: EventParam.Google,
        response: EventParam.Success,
      });
      await this.navigateAfterSignIn();
    });
  }

  signInWithApple(): Job<void> {
    return this.jobService.create(async () => {
      await this.withFirebaseGuard(
        () => FirebaseAuthentication.signInWithApple(),
        'Auth signInWithApple',
      );
      this.analyticsService.log(EventName.SignInApiResponse, {
        provider: EventParam.Apple,
        response: EventParam.Success,
      });
      await this.navigateAfterSignIn();
    });
  }

  signInWithEmailLink(email: string, emailLink: string): Job<void> {
    return this.jobService.create(async () => {
      await this.withFirebaseGuard(
        () =>
          FirebaseAuthentication.signInWithEmailLink({
            email,
            emailLink,
          }),
        'Auth signInWithEmailLink',
      );
      this.analyticsService.log(EventName.SignInApiResponse, {
        provider: EventParam.EmailLink,
        response: EventParam.Success,
      });
      await this.navigateAfterSignIn();
    });
  }

  signInWithEmail(email: string, password: string): Job<void> {
    return this.jobService.create(async () => {
      await this.withFirebaseGuard(
        () =>
          FirebaseAuthentication.signInWithEmailAndPassword({
            email,
            password,
          }),
        'Auth signInWithEmail',
      );
      await this.navigateAfterSignIn();
    });
  }

  signInWithToken(token: string): Job<void> {
    return this.jobService.create(async () => {
      await this.withFirebaseGuard(
        () => FirebaseAuthentication.signInWithCustomToken({ token }),
        'Auth signInWithToken',
      );
    });
  }

  async signOut(): Promise<void> {
    await this.storageService.removeItem(AUTH_USER);
    await this.signOutService.signOut();

    await this.withFirebaseGuard(() => FirebaseAuthentication.signOut(), 'Auth signOut');
  }

  private navigateAfterSignIn() {
    return this.router.navigate([PAGE.CHOOSE_GAME]);
  }

  loadCurrentUser(): Job<boolean> {
    return this.jobService.create(async () => {
      const userResult = await this.withFirebaseGuard(
        () => FirebaseAuthentication.getCurrentUser(),
        'Auth loadCurrentUser',
      );
      if (userResult.user) {
        await this.setUser(userResult.user.uid);
        return true;
      }
      return false;
    });
  }

  private async setUser(userId: string) {
    try {
      const hasClinchUserId = await Token.hasClinchUserId();
      if (!hasClinchUserId) {
        await WagerApi.request(this.httpService, this.analyticsService).setProvisionalUser();

        // Force refresh token so that the custom claims is set within the app
        await Token.get(true);
      }
    } catch (error) {
      this.analyticsService.handleError(error, 'Set provisional user');
    }

    await this.storageService.setItem(AUTH_USER, userId);

    const version = config.plugins?.['CapacitorUpdater']?.['version'];
    if (version) {
      this.analyticsService.setProperty('appVersion', version);
    }
    this.analyticsService.setProperty('build', environment.build.toString());

    if (Capacitor.isNativePlatform()) {
      this.analyticsService.setProperty('bundleId', Capacitor.getPlatform());
      this.analyticsService.signIn(userId);
    } else {
      if (environment.telegram) {
        this.analyticsService.setProperty('bundleId', 'gg.clinch.telegram');
        this.analyticsService.log(EventName.OperatorInitialised, {
          operatorId: 'telegram',
          userId: userId,
        });
        this.analyticsService.signIn(userId);
      } else {
        this.analyticsService.setProperty('bundleId', 'gg.clinch.play');
        this.analyticsService.signIn(userId);
      }
    }

    const walletId = this.profileService.walletId;
    if (walletId) {
      this.analyticsService.setProperty('walletId', walletId);
    }

    try {
      CapacitorUpdater.setCustomId({ customId: userId });
    } catch (error) {
      this.analyticsService.handleError(error, 'Set Capacitor customId');
    }

    this.dataLayerService.setUserId(userId);

    const job = this.jobService
      .create(async () => {
        await this.profileService.initialise(userId);
      })
      .showNoInternetRetry(Job.InfiniteRetry);
    job
      .addErrorHandler()
      .showFatalError()
      .onCancel(() => this.signOut());
    await job.runNoThrow();
  }

  private async withFirebaseGuard<T>(operation: () => Promise<T>, context: string): Promise<T> {
    try {
      return await operation();
    } catch (error) {
      this.analyticsService.handleError(error, context);
      throw error;
    }
  }
}
